'use client';

import { useCallback, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useTranslations } from 'next-intl';
import { MdRefresh, MdShield } from 'react-icons/md';

import { BottomSheet } from '@/components/ui/bottom-sheet';
import { useFocusTrap } from '@/hooks/useFocusTrap';
import { useMediaQuery } from '@/hooks/useMediaQuery';
import { PuzzleSolvedProvider } from './PuzzleFrame';
import { pickRandomPuzzle } from './puzzles';
import type { PuzzleDefinition } from './puzzles';
import {
  challengeBody,
  challengeDialog,
  challengeHeader,
  challengeHeaderText,
  challengeOverlay,
  challengeRefreshButton,
  challengeTitle,
} from './CaptchaChallenge.style';
import type { CaptchaChallengeProps } from './CaptchaChallenge.types';

const DESKTOP_QUERY = '(min-width: 768px)';

/**
 * How long the solved state stays on screen before the request is released.
 *
 * Long enough to read the confirmation, short enough not to feel like a stall on top of a
 * failure the customer did not cause. The replay is already in flight behind it.
 */
const SOLVED_DWELL_MS = 700;

/** `BottomSheet.onClose` is required by its props but unreachable while `dismissible` is false. */
const NO_DISMISS = () => {};

/**
 * The bot-check modal: one randomly chosen puzzle, standing in front of a parked API request.
 *
 * ## Invariants:
 * - Branches on `useMediaQuery`, never CSS visibility. Why: both branches portal to
 *   `document.body`, so a `tw:hidden` wrapper does not contain them and BOTH would mount at
 *   once — the portal landmine in this repo's CLAUDE.md.
 * - The puzzle is chosen once per opening, held in state, and only replaced by an explicit
 *   refresh. Why: choosing during render reshuffles the challenge on every keystroke and drag
 *   frame, so it could never be completed.
 * - `onSettle` fires exactly once per opening, guarded by a ref rather than state. Why: it
 *   releases a parked HTTP request; a second call would replay the same payment twice. A state
 *   flag is read from a stale closure by the dwell timer, a ref is not.
 * - Strictly modal: no close control, and backdrop click and Escape do nothing. Why: the request
 *   behind it is parked mid-flow — a payment, a booking — and a dismissal dropped the customer
 *   onto a screen whose state no longer matched what had actually happened. The way out is to
 *   finish the check, or to reroll it with the refresh control; a customer who cannot get past it
 *   spends the attempt budget and gets the reload popup, which is an explained dead end rather
 *   than a silent one.
 * - `onSettle` therefore only ever reports `true` from here. The `false` outcome still exists in
 *   the broker's contract (no UI mounted, or the terminal popup taking over) — it is simply not
 *   something the customer can trigger.
 */
export const CaptchaChallenge = ({ open, onSettle }: CaptchaChallengeProps) => {
  const t = useTranslations('Captcha');
  const isDesktop = useMediaQuery(DESKTOP_QUERY);
  const dialogRef = useRef<HTMLDivElement>(null);
  const settledRef = useRef(false);

  const [puzzle, setPuzzle] = useState<PuzzleDefinition>(pickRandomPuzzle);
  // Bumped on refresh so the puzzle remounts and rerolls its own internal answer.
  const [attempt, setAttempt] = useState(0);
  const [solved, setSolved] = useState(false);

  // Adjust-state-during-render rather than an effect: an effect would render one frame of the
  // previous challenge's solved state before resetting it.
  const [wasOpen, setWasOpen] = useState(open);
  if (open !== wasOpen) {
    setWasOpen(open);
    if (open) {
      setSolved(false);
      setPuzzle(pickRandomPuzzle());
      setAttempt((n) => n + 1);
    }
  }

  // Arming the settle guard is an effect rather than part of the block above because writing a
  // ref during render is forbidden. Safe here: nothing can call `settle` until the customer
  // interacts or the dwell timer fires, and both happen after the commit this effect runs in.
  useEffect(() => {
    if (open) settledRef.current = false;
  }, [open]);

  const settle = useCallback(
    (didSolve: boolean) => {
      if (settledRef.current) return;
      settledRef.current = true;
      onSettle(didSolve);
    },
    [onSettle]
  );

  const handleSolved = useCallback(() => {
    setSolved(true);
  }, []);

  useEffect(() => {
    if (!solved) return;
    const timer = window.setTimeout(() => settle(true), SOLVED_DWELL_MS);
    return () => window.clearTimeout(timer);
  }, [settle, solved]);

  const handleRefresh = useCallback(() => {
    setSolved(false);
    setPuzzle(pickRandomPuzzle());
    setAttempt((n) => n + 1);
  }, []);

  // Keyboard focus stays inside the dialog; the desktop overlay covers the page, so nothing
  // behind the challenge is reachable by pointer either. No Escape listener by design.
  useFocusTrap(dialogRef, open && isDesktop);

  if (!open) return null;

  const { Component } = puzzle;

  const refreshButton = (
    <button
      type="button"
      onClick={handleRefresh}
      className={challengeRefreshButton}
      aria-label={t('newChallenge')}
      data-cy="captcha-challenge-refresh"
    >
      <MdRefresh size={18} aria-hidden="true" />
    </button>
  );

  const body = (
    <PuzzleSolvedProvider value={solved}>
      <div className={challengeBody} data-cy={`captcha-puzzle-${puzzle.id}`}>
        <Component key={attempt} onSolved={handleSolved} />
      </div>
    </PuzzleSolvedProvider>
  );

  if (isDesktop) {
    return createPortal(
      <div className={challengeOverlay} data-cy="captcha-challenge-overlay">
        <div
          ref={dialogRef}
          role="dialog"
          aria-modal="true"
          aria-label={t('title')}
          className={challengeDialog}
          data-cy="captcha-challenge-dialog"
        >
          <div className={challengeHeader}>
            <MdShield
              size={20}
              className="tw:mt-nano tw:shrink-0 tw:text-primary"
              aria-hidden="true"
            />
            <div className={challengeHeaderText}>
              <span className={challengeTitle}>{t('title')}</span>
            </div>
            {refreshButton}
          </div>
          {body}
        </div>
      </div>,
      document.body
    );
  }

  return (
    <BottomSheet
      open={open}
      // Non-dismissible, so nothing can call this — the sheet renders no close control and
      // ignores backdrop and Escape. Its focus trap and scroll lock still apply.
      onClose={NO_DISMISS}
      dismissible={false}
      title={t('title')}
      // BottomSheet pads its header but not its body, so the puzzle supplies its own gutter on
      // all four sides — `pt` included, or the prompt sits flush against the header rule.
      bodyClassName="tw:flex tw:flex-col tw:gap-small tw:px-base tw:pt-base tw:pb-base"
      data-cy="captcha-challenge-sheet"
    >
      {/* `justify-end` is load-bearing: the removed subtitle carried the `flex-1` that pushed
          this button to the right edge. It is also the ONLY control on a sheet that renders no
          close affordance and ignores backdrop and Escape, so it must stay reachable and where
          the customer expects it — see the module CLAUDE.md on the refresh control. */}
      <div className="tw:flex tw:items-center tw:justify-end tw:gap-micro">{refreshButton}</div>
      {body}
    </BottomSheet>
  );
};

CaptchaChallenge.displayName = 'CaptchaChallenge';
